import keys from 'lodash/keys.js';
import { createStore } from 'vuex';

import autosaver from '@/plugins/autosaver/index.js';
import VuexOrmPlugin from '@/plugins/vuex-orm.js';
import { ability, abilityPlugin } from '@/store/Ability.js';
import Alerts from '@/store/quill/Alerts.js';

const files = import.meta.globEager('@/store/modules/**/*.js');

const storeModules = Object.keys(files)
    .filter(file => !file.startsWith('./modules/base'))
    .map(file => [file.replace(/(^.\/modules\/)|(\.js$)/g, ''), files[file]])
    .filter(([, moduleFile]) => {
        const moduleConfig = { ...moduleFile.default };

        return moduleConfig.autoImport === undefined ? true : moduleConfig.autoImport;
    })
    .reduce((acc, [namespace, moduleFile]) => {
        const moduleConfig = { ...moduleFile.default };

        if (moduleConfig.namespaced === undefined) {
            moduleConfig.namespaced = true;
        }

        const name = namespace.split('/').pop();

        return { ...acc, [name]: moduleConfig };
    }, {});

export default createStore({
    modules: {
        ...storeModules,
        Alerts,
    },

    plugins: [abilityPlugin, autosaver, VuexOrmPlugin],

    state: {
        loading: false,
        logo: null,
        timezone: null,
        isOutOfDate: false,
        automatedSurveyAssignmentReady: null,
        sidebar: null,
        sidebarItems: null,
        alertDrawer: null,
        paginateMeta: {
            current_page: 1,
            from: 1,
            last_page: 1,
            path: '',
            per_page: 15,
            to: 15,
            total: 15,
        },
        ability,
        widgetDrawer: false,
        activeWidgetPanel: null,
        globalErrors: [],
        unreadNotifications: 0,
        bellNotificationsPage: 1,
        features: null,
    },

    getters: {
        isLoading(state) {
            return state.loading;
        },

        institutionLogo(state) {
            return state.logo;
        },

        deviceAssetId() {
            const el = document.querySelector('div[device-asset-id]');
            return el ? el.getAttribute('device-asset-id') : null;
        },

        deviceAssetHeader(state, getters) {
            const { deviceAssetId } = getters;
            return deviceAssetId ? { headers: { 'x-stanbridge-classroom': deviceAssetId } } : {};
        },
    },

    actions: {
        async hydrateInitialState({ state, dispatch }) {
            // eslint-disable-next-line no-underscore-dangle
            const initial = window.__INITIAL_STATE__ || {};

            if (initial) {
                await Promise.all(
                    keys(state).map(module => {
                        if (!state[module] || !initial[module]) return Promise.resolve();
                        return dispatch(`${module}/hydrate`, initial[module], { root: true });
                    })
                );

                dispatch('AppRuntime/setTimestamp', {}, { root: true });
            }
        },
    },

    mutations: {
        TOGGLE_LOADING(state) {
            state.loading = !state.loading;
        },

        SET_OUT_OF_DATE(state) {
            state.isOutOfDate = true;
        },

        SET_AUTOMATED_SURVEY_ASSIGNMENT_READY(state, route) {
            state.automatedSurveyAssignmentReady = route;
        },

        SET_SIDEBAR(state, identifier) {
            state.sidebar = identifier;
        },

        SET_SIDEBAR_ITEMS(state, items) {
            state.sidebarItems = items;
        },

        SET_ALERT_DRAWER(state, payload) {
            state.alertDrawer = payload.state;
        },

        SET_PAGINATE_META(state, payload) {
            state.paginateMeta = payload;
        },

        SET_WIDGET_DRAWER(state, payload) {
            state.widgetDrawer = payload.state;
        },

        SET_ACTIVE_WIDGET_PANEL(state, payload) {
            state.activeWidgetPanel = payload.id;
        },

        PUSH_GLOBAL_ERROR(state, payload) {
            state.globalErrors = [...state.globalErrors, payload];
        },

        DISMISS_GLOBAL_ERROR(state, index) {
            state.globalErrors.splice(index, 1);
        },

        CLEAR_GLOBAL_ERRORS(state) {
            state.globalErrors = [];
        },

        SET_UNREAD_NOTIFICATIONS(state, payload) {
            state.unreadNotifications = payload;
        },

        SET_BELL_NOTIFICATIONS_PAGE(state, payload) {
            state.bellNotificationsPage = payload;
        },

        SET_FEATURES(state, payload) {
            state.features = payload;
        },
    },
});
